/**
 * 角色信息与聊天统计 IndexedDB
 * 用于持久化存储角色个人信息、聊天次数等数据
 */

export interface RoleProfile {
    // 角色基础信息（来自 RoleList）
    id: number
    name: string
    description: string
    personality: string
    firstMes: string
    avatar: string
    scenario: string
    backgroundThumb: string
    background: string
    creatorNotes: string
    systemPrompt: string
    tags: string[]
    characterBook: null
    state: number
    isRecommend: number
    chatCount: number
    chatCountVirtual: number
    favoriteCount: number
    isFavorite: boolean
  
    // 本地扩展字段
    lastChatTime?: number  // 最后聊天时间
    createdAt?: number     // 首次进入聊天页时间
    updatedAt?: number     // 最后更新时间
    isVip?: number         // 是否VIP角色
    userId?: string        // 当前用户ID（用于区分不同用户的数据）
  }
  
  export interface RoleChatStats {
    roleId: number
    userId?: string
  
    // 聊天次数统计
    chatCount: number           // 总聊天次数（API返回的）
    localChatCount: number      // 本地记录的聊天次数
  
    // 时间统计
    firstChatTime?: number      // 首次聊天时间
    lastChatTime?: number       // 最后聊天时间
    totalChatDays?: number      // 聊天总天数
    consecutiveDays?: number     // 连续聊天天数
  
    // 其他统计
    totalMessages?: number      // 总消息数
    lastMessagePreview?: string // 最后一条消息预览
  
    updatedAt?: number
  }
  
  class RoleProfileDB {
    private db: IDBDatabase | null = null
    private readonly dbName = 'RoleProfileDB'
    private readonly version = 1
  
    /**
     * 初始化数据库
     */
    async init(): Promise<void> {
      if (this.db) {
        return
      }
  
      return new Promise<void>((resolve, reject) => {
        const req = indexedDB.open(this.dbName, this.version)
  
        req.onerror = () => {
          console.error('❌ RoleProfileDB 打开失败:', req.error)
          reject(req.error)
        }
  
        req.onsuccess = () => {
          this.db = req.result
          console.log('✅ RoleProfileDB 初始化成功')
          resolve()
        }
  
        req.onupgradeneeded = event => {
          const db = (event.target as IDBOpenDBRequest).result
  
          // 角色基础信息表
          if (!db.objectStoreNames.contains('roleProfiles')) {
            const profileStore = db.createObjectStore('roleProfiles', { keyPath: 'id' })
            profileStore.createIndex('name', 'name', { unique: false })
            profileStore.createIndex('isVip', 'isVip', { unique: false })
            profileStore.createIndex('lastChatTime', 'lastChatTime', { unique: false })
            profileStore.createIndex('updatedAt', 'updatedAt', { unique: false })
            console.log('📦 创建角色基础信息表 (roleProfiles)')
          }
  
          // 角色聊天统计表
          if (!db.objectStoreNames.contains('roleChatStats')) {
            const statsStore = db.createObjectStore('roleChatStats', { keyPath: 'roleId' })
            statsStore.createIndex('userId', 'userId', { unique: false })
            statsStore.createIndex('lastChatTime', 'lastChatTime', { unique: false })
            statsStore.createIndex('localChatCount', 'localChatCount', { unique: false })
            console.log('📦 创建角色聊天统计表 (roleChatStats)')
          }
        }
      })
    }
  
    /**
     * 获取事务
     */
    private getTransaction(storeNames: string[], mode: IDBTransactionMode): IDBTransaction {
      if (!this.db) throw new Error('RoleProfileDB 未初始化，请先调用 init()')
      return this.db.transaction(storeNames, mode)
    }
  
    /**
     * Promise 化 IDBRequest
     */
    private promisify<T>(req: IDBRequest<T>): Promise<T> {
      return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result as T)
        req.onerror = () => reject(req.error)
      })
    }
  
    // ==================== 角色基础信息操作 ====================
  
    /**
     * 保存角色基础信息
     */
    async saveRoleProfile(profile: RoleProfile): Promise<void> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readwrite')
      const store = tx.objectStore('roleProfiles')
      const data: RoleProfile = {
        ...profile,
        updatedAt: Date.now()
      }
      await this.promisify(store.put(data))
    }
  
    /**
     * 批量保存角色基础信息
     */
    async saveRoleProfiles(profiles: RoleProfile[]): Promise<void> {
      if (profiles.length === 0) return
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readwrite')
      const store = tx.objectStore('roleProfiles')
      const now = Date.now()
      for (const profile of profiles) {
        await this.promisify(store.put({ ...profile, updatedAt: now }))
      }
    }
  
    /**
     * 获取角色基础信息
     */
    async getRoleProfile(id: number): Promise<RoleProfile | null> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readonly')
      const result = await this.promisify<RoleProfile | undefined>(
        tx.objectStore('roleProfiles').get(id)
      )
      return result || null
    }
  
    /**
     * 获取所有角色基础信息
     */
    async getAllRoleProfiles(): Promise<RoleProfile[]> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readonly')
      return this.promisify<RoleProfile[]>(tx.objectStore('roleProfiles').getAll())
    }
  
    /**
     * 获取最近聊天的角色（按最后聊天时间倒序）
     */
    async getRecentChatRoles(limit: number = 20): Promise<RoleProfile[]> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readonly')
      const store = tx.objectStore('roleProfiles')
      const all = await this.promisify<RoleProfile[]>(store.getAll())
  
      return all
        .filter(p => p.lastChatTime && p.lastChatTime > 0)
        .sort((a, b) => (b.lastChatTime || 0) - (a.lastChatTime || 0))
        .slice(0, limit)
    }
  
    /**
     * 删除角色基础信息
     */
    async deleteRoleProfile(id: number): Promise<void> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles'], 'readwrite')
      await this.promisify(tx.objectStore('roleProfiles').delete(id))
    }
  
    /**
     * 更新角色最后聊天时间
     */
    async updateLastChatTime(id: number): Promise<void> {
      await this.init()
      const profile = await this.getRoleProfile(id)
      if (profile) {
        profile.lastChatTime = Date.now()
        profile.updatedAt = Date.now()
        await this.saveRoleProfile(profile)
      }
    }
  
    // ==================== 角色聊天统计操作 ====================
  
    /**
     * 保存角色聊天统计
     */
    async saveRoleChatStats(stats: RoleChatStats): Promise<void> {
      await this.init()
      const tx = this.getTransaction(['roleChatStats'], 'readwrite')
      const store = tx.objectStore('roleChatStats')
      const data: RoleChatStats = {
        ...stats,
        updatedAt: Date.now()
      }
      await this.promisify(store.put(data))
    }
  
    /**
     * 获取角色聊天统计
     */
    async getRoleChatStats(roleId: number): Promise<RoleChatStats | null> {
      await this.init()
      const tx = this.getTransaction(['roleChatStats'], 'readonly')
      const result = await this.promisify<RoleChatStats | undefined>(
        tx.objectStore('roleChatStats').get(roleId)
      )
      return result || null
    }
  
    /**
     * 获取所有角色聊天统计
     */
    async getAllRoleChatStats(): Promise<RoleChatStats[]> {
      await this.init()
      const tx = this.getTransaction(['roleChatStats'], 'readonly')
      return this.promisify<RoleChatStats[]>(tx.objectStore('roleChatStats').getAll())
    }
  
    /**
     * 增加本地聊天次数
     */
    async incrementLocalChatCount(roleId: number): Promise<number> {
      await this.init()
  
      let stats = await this.getRoleChatStats(roleId)
      const now = Date.now()
  
      if (!stats) {
        stats = {
          roleId,
          chatCount: 0,
          localChatCount: 1,
          firstChatTime: now,
          lastChatTime: now,
          updatedAt: now
        }
      } else {
        stats.localChatCount = (stats.localChatCount || 0) + 1
        stats.lastChatTime = now
        stats.updatedAt = now
  
        // 首次聊天时间
        if (!stats.firstChatTime) {
          stats.firstChatTime = now
        }
      }
  
      await this.saveRoleChatStats(stats)
      return stats.localChatCount
    }
  
    /**
     * 获取角色本地聊天次数
     */
    async getLocalChatCount(roleId: number): Promise<number> {
      const stats = await this.getRoleChatStats(roleId)
      return stats?.localChatCount || 0
    }
  
    /**
     * 删除角色聊天统计
     */
    async deleteRoleChatStats(roleId: number): Promise<void> {
      await this.init()
      const tx = this.getTransaction(['roleChatStats'], 'readwrite')
      await this.promisify(tx.objectStore('roleChatStats').delete(roleId))
    }
  
    // ==================== 批量操作 ====================
  
    /**
     * 删除角色的所有数据（基础信息 + 统计）
     */
    async deleteRoleAllData(roleId: number): Promise<void> {
      await Promise.all([
        this.deleteRoleProfile(roleId),
        this.deleteRoleChatStats(roleId)
      ])
      console.log(`🗑️ 已删除角色 ${roleId} 的所有数据`)
    }
  
    /**
     * 清空所有数据
     */
    async clearAll(): Promise<void> {
      await this.init()
      const tx = this.getTransaction(['roleProfiles', 'roleChatStats'], 'readwrite')
      await Promise.all([
        this.promisify(tx.objectStore('roleProfiles').clear()),
        this.promisify(tx.objectStore('roleChatStats').clear())
      ])
      console.log('🗑️ 已清空所有角色信息数据')
    }
  
    /**
     * 获取统计数据概览
     */
    async getStatsOverview(): Promise<{
      totalRoles: number
      totalChats: number
      totalMessages: number
      mostChattedRoleId: number | null
      mostChattedCount: number
    }> {
      await this.init()
      const [profiles, stats] = await Promise.all([
        this.getAllRoleProfiles(),
        this.getAllRoleChatStats()
      ])
  
      const totalRoles = profiles.length
      const totalChats = stats.reduce((sum, s) => sum + (s.localChatCount || 0), 0)
      const totalMessages = stats.reduce((sum, s) => sum + (s.totalMessages || 0), 0)
  
      let mostChattedRoleId: number | null = null
      let mostChattedCount = 0
      for (const s of stats) {
        if ((s.localChatCount || 0) > mostChattedCount) {
          mostChattedCount = s.localChatCount || 0
          mostChattedRoleId = s.roleId
        }
      }
  
      return { totalRoles, totalChats, totalMessages, mostChattedRoleId, mostChattedCount }
    }
  }
  
  // 导出单例
  export const roleProfileDB = new RoleProfileDB()
  